/** Closed/cancelled outage history browser (spec §4.4): paged read of
 *  GET /oms/outages/history with cause/feeder filters + an admin CSV export. */
import { useState } from 'react'
import { isApiError } from '../../api/client.ts'
import { downloadOmsReport } from '../../api/oms.ts'
import { useOutageHistory } from '../../hooks/useOms.ts'
import { CAUSE_CODE_LABELS } from '../../types/oms.ts'
import type { CauseCode } from '../../types/oms.ts'
import {
  StatusBadge,
  causeText,
  deviceLabel,
  formatDateTime,
  formatDuration,
} from './omsFormat.tsx'
import { OmsSubNav } from './OmsSubNav.tsx'

const PAGE_SIZE = 25

export function OutageHistoryPage() {
  const [causeCode, setCauseCode] = useState<CauseCode | ''>('')
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null])
  const [page, setPage] = useState(0)
  const [exportError, setExportError] = useState<string | null>(null)

  const { data, isLoading, isError } = useOutageHistory({
    cause_code: causeCode || undefined,
    limit: PAGE_SIZE,
    cursor: cursorStack[page] ?? undefined,
  })
  const items = data?.items ?? []
  const hasNext = data?.next_cursor != null

  async function onExport() {
    setExportError(null)
    try {
      await downloadOmsReport(
        `/oms/outages/history?format=csv${causeCode ? `&cause_code=${causeCode}` : ''}`,
        'oms_outage_history.csv',
      )
    } catch (e) {
      setExportError(isApiError(e) ? e.detail : 'Export failed.')
    }
  }

  function resetPaging() {
    setCursorStack([null])
    setPage(0)
  }

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <OmsSubNav />
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Outage history</h2>
            <p className="text-sm text-gray-500">Closed and cancelled events.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={causeCode}
              onChange={e => {
                setCauseCode(e.target.value as CauseCode | '')
                resetPaging()
              }}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
            >
              <option value="">All causes</option>
              {(Object.keys(CAUSE_CODE_LABELS) as CauseCode[]).map(c => (
                <option key={c} value={c}>{CAUSE_CODE_LABELS[c]}</option>
              ))}
            </select>
            <button
              onClick={onExport}
              className="text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Export CSV
            </button>
          </div>
        </div>

        {exportError && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {exportError}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="text-sm text-gray-400 py-8 text-center">Loading history…</div>
          ) : isError ? (
            <div className="text-sm text-red-600 py-8 text-center">
              Failed to load outage history.
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">
              No closed or cancelled outages match.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr className="border-b border-gray-200">
                    <th className="p-2">#</th>
                    <th className="p-2">Area</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Cause</th>
                    <th className="p-2">Started</th>
                    <th className="p-2">Duration</th>
                    <th className="p-2 text-right">Customers</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(o => (
                    <tr key={o.id} className="border-b border-gray-50">
                      <td className="p-2 font-mono text-xs text-gray-500">#{o.id}</td>
                      <td className="p-2">
                        {deviceLabel(o.scope, o.device_id, o.unresolved_meter_number)}
                      </td>
                      <td className="p-2"><StatusBadge status={o.status} /></td>
                      <td className="p-2">{causeText(o)}</td>
                      <td className="p-2">{formatDateTime(o.start_time)}</td>
                      <td className="p-2">{formatDuration(o.duration_seconds)}</td>
                      <td className="p-2 text-right">{o.customers_affected ?? '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-[10px] text-gray-400">Page {page + 1}</span>
            <button
              onClick={() => {
                if (!hasNext || data?.next_cursor == null) return
                setCursorStack(prev => {
                  const next = prev.slice(0, page + 1)
                  next.push(data.next_cursor)
                  return next
                })
                setPage(p => p + 1)
              }}
              disabled={!hasNext}
              className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
